import { useMemo } from "react";
import { useCanAccess } from "ra-core";

import { useAllRecords } from "../agencies/allRecords";
import type { Agency, AgencyPortalUser } from "../types";

const DIRECTORY_STALE_MS = 5 * 60 * 1000;

/** Shared with the Agencies tab (same react-query keys, one download). */
export const AGENCIES_SORT = { field: "name", order: "ASC" } as const;
export const PORTAL_USERS_SORT = {
  field: "created_at",
  order: "DESC",
} as const;

export type StudioDirectory = {
  /** False for non-admins: agencies and portal logins are admin-only. */
  available: boolean;
  isPending: boolean;
  agencies: Agency[];
  /** Usable wherever an `AgencyLookup` / `LoginLookup` is expected. */
  agenciesById: ReadonlyMap<string, Agency>;
  loginsById: ReadonlyMap<string, AgencyPortalUser>;
};

/**
 * Agency names and portal-login e-mails for Studio films (B6.3). The dashboard
 * rows carry only ids (plan G10: no e-mails in the dashboard database); the
 * admin-only CRM resolves them from Supabase. Non-admins get empty maps and
 * the UI falls back to "Studio film" / "Portal user …".
 */
export function useStudioDirectory({
  enabled = true,
}: { enabled?: boolean } = {}): StudioDirectory {
  const { canAccess: canListAgencies } = useCanAccess({
    resource: "agencies",
    action: "list",
  });
  const { canAccess: canListLogins } = useCanAccess({
    resource: "agency_portal_users",
    action: "list",
  });
  const agencies = useAllRecords<Agency>("agencies", {
    sort: AGENCIES_SORT,
    enabled: enabled && canListAgencies === true,
    staleTime: DIRECTORY_STALE_MS,
  });
  const logins = useAllRecords<AgencyPortalUser>("agency_portal_users", {
    sort: PORTAL_USERS_SORT,
    enabled: enabled && canListLogins === true,
    staleTime: DIRECTORY_STALE_MS,
  });
  const agencyList = agencies.data ?? NO_AGENCIES;
  const agenciesById = useMemo(
    () => new Map(agencyList.map((agency) => [String(agency.id), agency])),
    [agencyList],
  );
  const loginsById = useMemo(
    () =>
      new Map((logins.data ?? []).map((login) => [String(login.id), login])),
    [logins.data],
  );
  const isPending = agencies.isPending || logins.isPending;
  const available = canListAgencies === true;
  // Stable while nothing changes: it is a context value for every list cell.
  return useMemo(
    () => ({
      available,
      isPending,
      agencies: agencyList,
      agenciesById,
      loginsById,
    }),
    [available, isPending, agencyList, agenciesById, loginsById],
  );
}

const NO_AGENCIES: Agency[] = [];
